import { useState, useEffect, useRef } from "react";
import AddBusinessModal from "../components/discover_components/create_business/AddBusinessModal";
import { Button as AddButton } from "../components/discover_components/create_business/Button";

import {
  Box,
  Container,
  Typography,
  Button,
  Autocomplete,
  Tabs,
  Tab,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import { styled } from "@mui/system";
import ReactDOM from "react-dom";
import CloseIcon from "@mui/icons-material/Close";
import { useIsMobile } from "../hooks/use-mobile";
import TabNavigation from "../components/discover_components/TabNavigation";
import MainLayout from "../components/MainLayout";
import ContentView from "../components/discover_components/ContentView";
import { tabs } from "../components/discover_components/data/tabs-data";
import { googleMapHandler, googleMapImageHandler } from "../services/googlemap";
import { successToast, errorToast } from "../utils/toasters";
import {
  XCircle,
  Navigation,
  MapPin,
  List,
  Map,
  Star,
  Plus,
} from "lucide-react";
import { Loader2 } from "lucide-react";

const StyledContainer = styled(Container)(({ theme }) => ({
  marginTop: theme.spacing(4),
  marginBottom: theme.spacing(4),
}));

const StyledAutocomplete = styled(Autocomplete)(({ theme }) => ({
  marginBottom: theme.spacing(2),
}));

const ResultItem = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
  borderBottom: `1px solid ${theme.palette.divider}`,
  cursor: "pointer",
  "&:hover": {
    backgroundColor: theme.palette.action.hover,
  },
}));

const NoResultsMessage = styled(Typography)(({ theme }) => ({
  padding: theme.spacing(2),
  fontStyle: "italic",
  color: theme.palette.text.secondary,
}));

const MapContainer = styled(Box)(({ theme }) => ({
  width: "100%",
  height: "500px",
  borderRadius: theme.shape.borderRadius,
  overflow: "hidden",
}));

const StyledTabs = styled(Tabs)(({ theme }) => ({
  marginBottom: theme.spacing(2),
}));

const StyledTab = styled(Tab)(({ theme }) => ({
  fontSize: "1rem",
  "&.Mui-selected": {
    fontWeight: theme.typography.fontWeightMedium,
  },
}));

const LocationDialog = ({ open, onClose, locationDetails }) => {
  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>
        Location Details
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        {locationDetails ? (
          <>
            <Typography variant="subtitle1">
              <strong>Name:</strong> {locationDetails.name}
            </Typography>
            <Typography variant="subtitle1">
              <strong>Address:</strong> {locationDetails.formatted_address}
            </Typography>
            {/* Add more details as needed */}
          </>
        ) : (
          <Typography>Loading details...</Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

// Google Maps Script Loader Component
const GoogleMapsScript = ({ apiKey }) => {
  useEffect(() => {
    // Check if script is already loaded
    if (window.google) return;

    // Create script element
    const script = document.createElement("script");
    script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places`;
    script.async = true;
    script.defer = true;

    // Add a flag to indicate when Google Maps is loaded
    script.onload = () => {
      window.googleMapsLoaded = true;
    };

    // Append script to document
    document.head.appendChild(script);

    // Clean up
    return () => {
      if (document.head.contains(script)) {
        document.head.removeChild(script);
      }
    };
  }, [apiKey]);

  return null;
};

// Add CSS for hiding scrollbar - Add this after the GoogleMapsScript component
const ScrollbarStyles = () => (
  <style jsx global>{`
    .hide-scrollbar {
      -ms-overflow-style: none; /* IE and Edge */
      scrollbar-width: none; /* Firefox */
    }
    .hide-scrollbar::-webkit-scrollbar {
      display: none; /* Chrome, Safari and Opera */
    }

    @keyframes fadeIn {
      from {
        opacity: 0;
        transform: translateY(10px);
      }
      to {
        opacity: 1;
        transform: translateY(0);
      }
    }

    .animate-fadeIn {
      animation: fadeIn 0.3s ease-out forwards;
    }

    .card-scroller-transition {
      transition: bottom 0.3s ease-in-out, opacity 0.3s ease-in-out;
    }

    .marker-active {
      transform: scale(1.1);
      transition: transform 0.3s ease;
    }

    .marker-inactive {
      opacity: 0.6;
      transition: opacity 0.3s ease;
    }

    .card-hover-effect {
      transition: opacity 0.2s ease, transform 0.3s ease;
    }

    .card-hover-effect:hover {
      opacity: 1 !important;
    }

    .detail-card-enter {
      opacity: 0;
      transform: translateY(20px);
    }

    .detail-card-enter-active {
      opacity: 1;
      transform: translateY(0);
      transition: opacity 300ms, transform 300ms;
    }

    .detail-card-exit {
      opacity: 1;
    }

    .detail-card-exit-active {
      opacity: 0;
      transform: translateY(20px);
      transition: opacity 300ms, transform 300ms;
    }

    /* Custom InfoWindow styles */
    .custom-info-window {
      position: absolute;
      background: white;
      border-radius: 8px;
      box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
      width: 360px;
      overflow: hidden;
      z-index: 1000;
      transform: translate(-50%, -100%);
      margin-top: -15px; /* Space above the marker */
      border: 1px solid rgba(0, 0, 0, 0.1);
    }

    .custom-info-window::after {
      content: "";
      position: absolute;
      bottom: -10px;
      left: 50%;
      transform: translateX(-50%);
      width: 0;
      height: 0;
      border-left: 10px solid transparent;
      border-right: 10px solid transparent;
      border-top: 10px solid white;
    }
  `}</style>
);

const apiKey = process.env.REACT_APP_GOOGLEMAP_API;

// Get category icon based on place type or category
const getCategoryIcon = (category) => {
  const iconMap = {
    Restaurants: "🍽️",
    Cafes: "☕",
    Bars: "🍸",
    Shopping: "🛍️",
    Entertainment: "🎭",
    Parks: "🌳",
    Gyms: "💪",
    Beauty: "💇",
    Hotels: "🏨",
    default: "🏪",
    Groceries: "🛒",
    Professionals: "👔",
    SmallBusiness: "🏪",
  };

  return iconMap[category] || iconMap.default;
};

// Get category letter icon based on place type or category
const getCategoryLetter = (category) => {
  const letterMap = {
    Restaurants: "R",
    Cafes: "C",
    Bars: "B",
    Shopping: "S",
    Entertainment: "E",
    Parks: "P",
    Gyms: "G",
    Beauty: "B",
    Hotels: "H",
    default: "P",
    Groceries: "G",
    Professionals: "P",
    SmallBusiness: "S",
  };

  return letterMap[category] || letterMap.default;
};

// Function to create marker SVG
const createMarkerSVG = (category, isActive) => {
  const categoryLetter = getCategoryIcon(category);

  // Create a marker that looks like a purple rounded square with a white letter
  return `data:image/svg+xml;charset=UTF-8,<svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 40 40">
    <!-- Shadow effect -->
    <filter id="shadow" x="-20%" y="-20%" width="140%" height="140%">
      <feDropShadow dx="0" dy="2" stdDeviation="2" floodOpacity="0.3" />
    </filter>
    
    <!-- Rounded square background -->
    <rect 
      x="2" 
      y="2" 
      width="36" 
      height="36" 
      rx="10" 
      ry="10" 
      fill="${isActive ? "%237b189f" : "%239333ea"}" 
      opacity="${isActive ? "1" : "0.9"}"
      filter="url(%23shadow)"
      stroke="${isActive ? "white" : "none"}"
      strokeWidth="${isActive ? "2" : "0"}"
    />
    
    <!-- Letter/Icon -->
    <text 
      x="50%" 
      y="50%" 
      fontFamily="Arial, sans-serif" 
      fontSize="${isActive ? "18" : "16"}" 
      fontWeight="bold"
      textAnchor="middle" 
      dominantBaseline="middle" 
      fill="white"
    >${categoryLetter}</text>
  </svg>`;
};

// Detailed place view card component - Horizontal layout
const DetailedPlaceCard = ({
  place,
  activeTab,
  location,
  onClose,
  onNavigate,
}) => {
  if (!place) return null;

  return (
    <div className="animate-fadeIn relative">
      {/* Close button at top right */}
      <button
        onClick={onClose}
        className="absolute top-2 right-2 bg-white/90 hover:bg-white rounded-full p-1 transition-colors z-10 shadow-sm"
      >
        <XCircle size={16} className="text-gray-700" />
      </button>

      {/* Horizontal card layout */}
      <div className="flex bg-white rounded-lg overflow-hidden">
        {/* Left side - Image */}
        <div className="w-1/3 relative h-[140px]">
          {place.photos && place.photos[0] ? (
            <img
              src={`https://maps.googleapis.com/maps/api/place/photo?maxwidth=400&photoreference=${place.photos[0].photo_reference}&key=${apiKey}`}
              alt={place.name}
              className="w-full h-full object-cover"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center bg-purple-100">
              <span className="text-3xl">{getCategoryIcon(activeTab)}</span>
            </div>
          )}

          {/* Rating badge */}
          {place.rating && (
            <div className="absolute top-2 left-2 bg-white rounded-md px-1.5 py-0.5 flex items-center shadow-sm">
              <Star className="h-3 w-3 text-amber-500 fill-amber-500" />
              <span className="text-xs font-medium ml-0.5">{place.rating}</span>
            </div>
          )}
        </div>

        {/* Right side - Content */}
        <div className="w-2/3 p-3 flex flex-col justify-between">
          {/* Place name and info */}
          <div>
            <h3 className="font-medium text-gray-800 text-base mb-1 line-clamp-1">
              {place.name}
            </h3>

            {/* Address */}
            {place.vicinity && (
              <div className="flex items-start mb-2">
                <MapPin
                  size={12}
                  className="text-purple-700 mt-0.5 flex-shrink-0"
                />
                <p className="text-gray-600 text-xs ml-1 line-clamp-2">
                  {place.vicinity}
                </p>
              </div>
            )}

            {/* Info badges */}
            <div className="flex flex-wrap gap-1 mb-2">
              <div className="flex items-center bg-purple-100 px-1.5 py-0.5 rounded-md">
                <span className="text-xs text-purple-700 font-medium">
                  {place.distance} mi
                </span>
              </div>

              {place.opening_hours && (
                <div
                  className={`px-1.5 py-0.5 rounded-md ${
                    place.opening_hours.open_now ? "bg-green-100" : "bg-red-100"
                  }`}
                >
                  <span
                    className={`text-xs font-medium ${
                      place.opening_hours.open_now
                        ? "text-green-700"
                        : "text-red-700"
                    }`}
                  >
                    {place.opening_hours.open_now ? "Open" : "Closed"}
                  </span>
                </div>
              )}

              {place.price_level && (
                <div className="bg-gray-100 px-1.5 py-0.5 rounded-md">
                  <span className="text-xs text-gray-700 font-medium">
                    {"$".repeat(place.price_level)}
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* Action buttons */}
          <div className="flex space-x-2 mt-auto">
            <a
              href={`https://www.google.com/maps/place/?q=place_id:${place.place_id}`}
              target="_blank"
              rel="noopener noreferrer"
              className="flex-1 bg-purple-700 text-white text-center py-1.5 rounded-md text-xs font-medium hover:bg-purple-800 transition-colors flex items-center justify-center"
            >
              <span>Details</span>
            </a>
            <button
              className="flex-1 border border-gray-300 text-gray-700 text-center py-1.5 rounded-md text-xs font-medium flex items-center justify-center gap-1 hover:bg-gray-50 transition-colors"
              onClick={onNavigate}
            >
              <Navigation size={12} />
              <span>Navigate</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Custom InfoWindow class - Modified to avoid the error
class CustomInfoWindow {
  constructor(position, content, map) {
    // Only initialize if Google Maps is loaded
    if (window.google && window.google.maps) {
      this.position = position;
      this.content = content;
      this.map = map;
      this.div = null;

      // Create the overlay
      this.overlay = new window.google.maps.OverlayView();

      // Set up the overlay methods
      this.overlay.onAdd = this.onAdd.bind(this);
      this.overlay.draw = this.draw.bind(this);
      this.overlay.onRemove = this.onRemove.bind(this);

      // Set the map
      this.overlay.setMap(map);
    } else {
      console.error("Google Maps API not loaded yet");
    }
  }

  onAdd() {
    // Create div for the info window
    const div = document.createElement("div");
    div.className = "custom-info-window";
    div.style.position = "absolute";
    div.style.display = "block";

    // Add the content to the div
    const contentContainer = document.createElement("div");
    ReactDOM.render(this.content, contentContainer);
    div.appendChild(contentContainer);

    this.div = div;

    // Add the div to the overlay layer
    const panes = this.overlay.getPanes();
    panes.overlayMouseTarget.appendChild(div);
  }

  draw() {
    // Position the div on the map
    const overlayProjection = this.overlay.getProjection();
    const point = overlayProjection.fromLatLngToDivPixel(this.position);

    if (this.div) {
      this.div.style.left = point.x + "px";
      this.div.style.top = point.y + "px";
    }
  }

  onRemove() {
    // Remove the div from the map
    if (this.div) {
      this.div.parentNode.removeChild(this.div);
      this.div = null;
    }
  }

  setContent(content) {
    this.content = content;
    if (this.div) {
      ReactDOM.unmountComponentAtNode(this.div);
      const contentContainer = document.createElement("div");
      ReactDOM.render(content, contentContainer);
      this.div.innerHTML = "";
      this.div.appendChild(contentContainer);
    }
  }

  setPosition(position) {
    this.position = position;
    this.draw();
  }

  setMap(map) {
    if (this.overlay) {
      this.overlay.setMap(map);
    }
  }
}

const DiscoverPage = () => {
  const [showAddBusiness, setShowAddBusiness] = useState(false);
  const isMobile = useIsMobile();
  const [activeTab, setActiveTab] = useState("Restaurants");
  const [activeSubTab, setActiveSubTab] = useState("");
  const [location, setLocation] = useState(null);
  const [error, setError] = useState(null);
  const [results, setResults] = useState([]);
  const [selectedPlace, setSelectedPlace] = useState(null);
  const [resultsWithDistance, setResultsWithDistance] = useState([]);
  const [resultsWithDistanceAndImages, setResultsWithDistanceAndImages] =
    useState([]);
  const [tryNewPlaces, setTryNewPlaces] = useState([]);
  const [currentMile, setCurrentMile] = useState(15);
  const [loading, setLoading] = useState(false);
  const [viewMode, setViewMode] = useState("list"); // "list" or "map"
  const [mapsLoaded, setMapsLoaded] = useState(false);
  const mapRef = useRef(null);
  const googleMapRef = useRef(null);
  const markersRef = useRef([]);
  const infoWindowRef = useRef(null);
  const [selectedPlaceId, setSelectedPlaceId] = useState(null);

  const handleTabChange = (tab, subTab = "") => {
    setActiveTab(tab);
    setActiveSubTab(subTab);
  };

  // Check if Google Maps is loaded
  useEffect(() => {
    const checkMapsLoaded = () => {
      if (window.google && window.google.maps) {
        setMapsLoaded(true);
      } else {
        setTimeout(checkMapsLoaded, 100);
      }
    };

    checkMapsLoaded();

    return () => {
      // Cleanup
    };
  }, []);

  const currentCategory = tabs.find((tab) => tab.title === activeTab);

  const calculateDistance = (lat1, lon1, lat2, lon2) => {
    const R = 3958.8; // Radius of the Earth in miles
    const dLat = ((lat2 - lat1) * Math.PI) / 180;
    const dLon = ((lon2 - lon1) * Math.PI) / 180;
    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos((lat1 * Math.PI) / 18) *
        Math.cos((lat2 * Math.PI) / 180) *
        Math.sin(dLon / 2) *
        Math.sin(dLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    const distance = R * c; // Distance in miles
    return distance.toFixed(1);
  };

  const extractTryNewPlaces = (places) => {
    if (!places || places.length === 0) return [];

    // Find places that are well-rated but not too popular (hidden gems)
    const potentialNewPlaces = places.filter(
      (place) =>
        place.rating &&
        place.rating >= 4.0 &&
        place.user_ratings_total &&
        place.user_ratings_total < 150
    );

    // Sort by a combination of rating and distance
    const sortedPlaces = [...potentialNewPlaces].sort((a, b) => {
      if (a.is_registered && !b.is_registered) return -1; // a comes first
      if (!a.is_registered && b.is_registered) return 1; // b comes first

      // Create a score based on rating and distance
      const scoreA = a.rating * 2 - Number.parseFloat(a.distance) * 0.1;
      const scoreB = b.rating * 2 - Number.parseFloat(b.distance) * 0.1;
      return scoreB - scoreA; // Higher score first
    });

    return sortedPlaces.slice(0, 8); // Limit to 8 places
  };

  // This function adds both distance and images to places
  const processPlacesWithDistanceAndImages = async (places, userLocation) => {
    if (!places || places.length === 0) return [];

    // First add distance to all places
    const placesWithDistance = places.map((place) => {
      const placeLat = place.geometry.location.lat;
      const placeLng = place.geometry.location.lng;

      const distance = calculateDistance(
        userLocation.latitude,
        userLocation.longitude,
        placeLat,
        placeLng
      );

      return {
        ...place,
        distance,
      };
    });

    // Then fetch and add images for all places
    const placesWithDistanceAndImages = await Promise.all(
      placesWithDistance.map(async (place) => {
        if (place.photos && place.photos.length > 0) {
          try {
            const photoReference = place.photos[0].photo_reference;
            const imageData = await googleMapImageHandler({
              photo_reference: photoReference,
              maxwidth: 400,
            });

            return {
              ...place,
              imageUrl: imageData || null,
            };
          } catch (error) {
            return {
              ...place,
              imageUrl: null,
            };
          }
        } else {
          return {
            ...place,
            imageUrl: null,
          };
        }
      })
    );

    return placesWithDistanceAndImages;
  };

  useEffect(() => {
    const getUserLocation = () => {
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          (position) => {
            setLocation({
              latitude: position.coords.latitude,
              longitude: position.coords.longitude,
            });
          },
          (err) => {
            setError("Please enable location services.");
            setLoading(false);
          }
        );
      } else {
        setError("Geolocation is not supported.");
        setLoading(false);
      }
    };

    getUserLocation();
  }, []);

  useEffect(() => {
    const fetchPlaces = async () => {
      if (!location || !currentCategory) return;
      setLoading(true);
      try {
        // Create base parameters object
        const params = {
          latitude: location.latitude,
          longitude: location.longitude,
          radius: currentMile * 1.6 * 1000,
          title: currentCategory.title,
          type: currentCategory.type,
          keyword: currentCategory.keyword,
        };

        // If it's Professionals category and a subcategory is selected,
        // use the subcategory as the keyword
        if (activeTab === "Professionals" && activeSubTab) {
          params.keyword = activeSubTab.keyword;
          params.type = activeSubTab.type;
        }

        // Make the API call with the appropriate parameters
        const data = await googleMapHandler(params);

        setResults(data || []);
      } catch (err) {
        errorToast("Something went wrong while fetching places.");
        setError("Something went wrong while fetching places.");
      } finally {
        setLoading(false);
      }
    };

    fetchPlaces();
  }, [location, activeTab, activeSubTab, currentMile, currentCategory]);

  useEffect(() => {
    const processResults = async () => {
      if (results.length > 0 && location) {
        setLoading(true);
        try {
          // First, set basic distance data for map view
          const basicPlacesWithDistance = results
            .map((place) => {
              const placeLat = place.geometry.location.lat;
              const placeLng = place.geometry.location.lng;

              const distance = calculateDistance(
                location.latitude,
                location.longitude,
                placeLat,
                placeLng
              );

              return {
                ...place,
                distance,
              };
            })
            .filter((place) => place.distance < currentMile); // Only include places where distance is less than currentMile

          // Update resultsWithDistance for map view
          setResultsWithDistance(basicPlacesWithDistance);

          // Process all places to add distance and images for list view
          const processedPlaces = await processPlacesWithDistanceAndImages(
            results,
            location
          );

          // Extract try new places from processed places
          const newPlaces = extractTryNewPlaces(processedPlaces);

          setResultsWithDistanceAndImages(processedPlaces);
          setTryNewPlaces(newPlaces);
        } catch (error) {
          errorToast("Error loading place details");
        } finally {
          setLoading(false);
        }
      } else {
        setResultsWithDistance([]);
        setResultsWithDistanceAndImages([]);
        setTryNewPlaces([]);
      }
    };

    processResults();
  }, [results, location]);

  // Handle closing the info window
  const handleCloseInfoWindow = () => {
    if (infoWindowRef.current) {
      infoWindowRef.current.setMap(null);
      infoWindowRef.current = null;
    }
    setSelectedPlace(null);

    // Reset all markers to default state
    markersRef.current.forEach((item) => {
      if (item.marker && window.google && window.google.maps) {
        item.marker.setIcon({
          url: createMarkerSVG(activeTab, false),
          scaledSize: new window.google.maps.Size(40, 40),
          anchor: new window.google.maps.Point(20, 20),
        });
        item.marker.setZIndex(1);
      }
    });
  };

  // Handle navigation to the place
  const handleNavigate = (place) => {
    if (!place || !location) return;

    window.open(
      `https://www.google.com/maps/dir/?api=1&origin=${location.latitude},${location.longitude}&destination=${place.geometry.location.lat},${place.geometry.location.lng}&destination_place_id=${place.place_id}`,
      "_blank"
    );
  };

  // Initialize Google Map - Completely refactored
  useEffect(() => {
    if (viewMode !== "map" || !location || !mapsLoaded || !mapRef.current)
      return;

    // Clear previous markers
    if (markersRef.current.length > 0) {
      markersRef.current.forEach((item) => {
        if (item.marker) item.marker.setMap(null);
      });
      markersRef.current = [];
    }

    // Close any existing info window
    if (infoWindowRef.current) {
      infoWindowRef.current.setMap(null);
      infoWindowRef.current = null;
    }

    // Initialize map
    const googleMap = new window.google.maps.Map(mapRef.current, {
      center: { lat: location.latitude, lng: location.longitude },
      zoom: 14,
      disableDefaultUI: isMobile, // Simplify UI on mobile
      zoomControl: !isMobile,
      mapTypeControl: false,
      streetViewControl: false,
      fullscreenControl: !isMobile,
    });

    googleMapRef.current = googleMap;

    // Add user location marker
    new window.google.maps.Marker({
      position: { lat: location.latitude, lng: location.longitude },
      map: googleMap,
      icon: {
        path: window.google.maps.SymbolPath.CIRCLE,
        scale: 8,
        fillColor: "#4285F4",
        fillOpacity: 1,
        strokeColor: "#FFFFFF",
        strokeWeight: 2,
      },
      title: "Your Location",
    });

    // Add markers for all places
    resultsWithDistance.forEach((place) => {
      // Create marker with new design
      const isActive =
        selectedPlace && selectedPlace.place_id === place.place_id;
      const marker = new window.google.maps.Marker({
        position: {
          lat: place.geometry.location.lat,
          lng: place.geometry.location.lng,
        },
        map: googleMap,
        title: place.name,
        // Use new marker design
        icon: {
          url: createMarkerSVG(activeTab, isActive),
          scaledSize: new window.google.maps.Size(
            isActive ? 48 : 40,
            isActive ? 48 : 40
          ),
          anchor: new window.google.maps.Point(
            isActive ? 24 : 20,
            isActive ? 24 : 20
          ),
        },
        opacity: 1, // Base opacity is always 1, we control it in the SVG
      });

      // Add to refs for cleanup with place_id for identification
      markersRef.current.push({
        marker,
        place_id: place.place_id,
        place: place,
      });

      // Add marker click listener to marker
      marker.addListener("click", () => {
        // Check if this place is already selected
        if (selectedPlace && selectedPlace.place_id === place.place_id) {
          // If it's already selected, close the info window (toggle off)
          handleCloseInfoWindow();
          return;
        }

        // Close existing info window if any
        if (infoWindowRef.current) {
          infoWindowRef.current.setMap(null);
          infoWindowRef.current = null;
        }

        // Set the selected place
        setSelectedPlace(place);
        setSelectedPlaceId(place.place_id);

        // Update all markers to show active/inactive state
        markersRef.current.forEach((item) => {
          const isActive = place.place_id === item.place_id;
          item.marker.setIcon({
            url: createMarkerSVG(activeTab, isActive),
            scaledSize: new window.google.maps.Size(
              isActive ? 48 : 40,
              isActive ? 48 : 40
            ),
            anchor: new window.google.maps.Point(
              isActive ? 24 : 20,
              isActive ? 24 : 20
            ),
          });
          item.marker.setZIndex(isActive ? 999 : 1); // Bring active marker to front
        });

        // Create and show the custom info window
        const content = (
          <DetailedPlaceCard
            place={place}
            activeTab={activeTab}
            location={location}
            onClose={handleCloseInfoWindow}
            onNavigate={() => handleNavigate(place)}
          />
        );

        // Create a new custom info window
        const infoWindow = new CustomInfoWindow(
          new window.google.maps.LatLng(
            place.geometry.location.lat,
            place.geometry.location.lng
          ),
          content,
          googleMap
        );

        infoWindowRef.current = infoWindow;
      });
    });

    // Add map click listener to close info windows when clicking elsewhere on the map
    googleMap.addListener("click", (e) => {
      // Check if the click is on a marker
      let clickedOnMarker = false;
      for (const item of markersRef.current) {
        if (item.marker.getPosition().equals(e.latLng)) {
          clickedOnMarker = true;
          break;
        }
      }

      // Only close the detail view if not clicking on a marker
      if (!clickedOnMarker) {
        handleCloseInfoWindow();
        setSelectedPlaceId(null);
      }
    });

    // If there's already a selected place, show its info window
    if (selectedPlace) {
      const markerItem = markersRef.current.find(
        (item) => item.place_id === selectedPlace.place_id
      );
      if (markerItem) {
        // Create and show the custom info window
        const content = (
          <DetailedPlaceCard
            place={selectedPlace}
            activeTab={activeTab}
            location={location}
            onClose={handleCloseInfoWindow}
            onNavigate={() => handleNavigate(selectedPlace)}
          />
        );

        // Create a new custom info window
        const infoWindow = new CustomInfoWindow(
          new window.google.maps.LatLng(
            selectedPlace.geometry.location.lat,
            selectedPlace.geometry.location.lng
          ),
          content,
          googleMap
        );

        infoWindowRef.current = infoWindow;

        // Update marker to active state
        markerItem.marker.setIcon({
          url: createMarkerSVG(activeTab, true),
          scaledSize: new window.google.maps.Size(48, 48),
          anchor: new window.google.maps.Point(24, 24),
        });
        markerItem.marker.setZIndex(999);
      }
    }
  }, [
    location,
    resultsWithDistance,
    isMobile,
    activeTab,
    viewMode,
    mapsLoaded,
  ]);

  // Update info window when selected place changes
  useEffect(() => {
    if (
      !googleMapRef.current ||
      viewMode !== "map" ||
      !window.google ||
      !mapsLoaded
    )
      return;

    // Close existing info window if any
    if (infoWindowRef.current) {
      infoWindowRef.current.setMap(null);
      infoWindowRef.current = null;
    }

    // Reset all markers to default state
    markersRef.current.forEach((item) => {
      if (item.marker) {
        item.marker.setIcon({
          url: createMarkerSVG(
            activeTab,
            selectedPlace && selectedPlace.place_id === item.place_id
          ),
          scaledSize: new window.google.maps.Size(
            selectedPlace && selectedPlace.place_id === item.place_id ? 48 : 40,
            selectedPlace && selectedPlace.place_id === item.place_id ? 48 : 40
          ),
          anchor: new window.google.maps.Point(
            selectedPlace && selectedPlace.place_id === item.place_id ? 24 : 20,
            selectedPlace && selectedPlace.place_id === item.place_id ? 24 : 20
          ),
        });
        item.marker.setZIndex(
          selectedPlace && selectedPlace.place_id === item.place_id ? 999 : 1
        );
      }
    });

    // If there's a selected place, show its info window
    if (selectedPlace) {
      const markerItem = markersRef.current.find(
        (item) => item.place_id === selectedPlace.place_id
      );
      if (markerItem) {
        // Center map on the selected place with smooth animation
        googleMapRef.current.panTo({
          lat: selectedPlace.geometry.location.lat,
          lng: selectedPlace.geometry.location.lng,
        });

        // Create and show the custom info window
        const content = (
          <DetailedPlaceCard
            place={selectedPlace}
            activeTab={activeTab}
            location={location}
            onClose={handleCloseInfoWindow}
            onNavigate={() => handleNavigate(selectedPlace)}
          />
        );

        // Create a new custom info window
        const infoWindow = new CustomInfoWindow(
          new window.google.maps.LatLng(
            selectedPlace.geometry.location.lat,
            selectedPlace.geometry.location.lng
          ),
          content,
          googleMapRef.current
        );

        infoWindowRef.current = infoWindow;
      }
    }
  }, [selectedPlace, activeTab, viewMode, mapsLoaded]);

  // Error display component
  const ErrorDisplay = () => (
    <div className="absolute inset-0 flex items-center justify-center bg-white bg-opacity-90">
      <div className="text-center p-4">
        <div className="text-red-500 text-xl mb-2">Error</div>
        <p className="text-gray-700">{error}</p>
        <button
          className="mt-4 bg-blue-500 text-white px-4 py-2 rounded"
          onClick={() => window.location.reload()}
        >
          Retry
        </button>
      </div>
    </div>
  );

  // MapCardScroller component - Simplified
  const MapCardScroller = ({ places, onCardClick, selectedPlaceId }) => {
    if (!places || places.length === 0) return null;

    return (
      <div
        className="absolute left-0 right-0 px-4 z-10"
        style={{
          bottom: selectedPlaceId ? "16px" : "24px",
          transition: "bottom 0.3s ease-in-out, opacity 0.3s ease-in-out",
          opacity: selectedPlaceId ? "0.7" : "1",
        }}
      >
        <div className="overflow-x-auto pb-2 hide-scrollbar">
          <div className="flex space-x-3">
            {places.map((place) => (
              <div
                key={place.place_id}
                className={`flex-shrink-0 w-64 bg-white rounded-lg shadow-lg overflow-hidden cursor-pointer transform transition-all duration-300 hover:scale-105 hover:opacity-100 ${
                  selectedPlaceId === place.place_id
                    ? "ring-2 ring-purple-700 scale-105 opacity-100"
                    : ""
                }`}
                style={{
                  opacity:
                    selectedPlaceId && selectedPlaceId !== place.place_id
                      ? "0.8"
                      : "1",
                  transition:
                    "transform 0.3s ease, opacity 0.3s ease, box-shadow 0.3s ease",
                }}
                onClick={() => {
                  onCardClick(place);
                  setSelectedPlaceId(place.place_id);
                }}
                onMouseEnter={(e) => {
                  if (selectedPlaceId) {
                    e.currentTarget.style.opacity = "1";
                  }
                }}
                onMouseLeave={(e) => {
                  if (selectedPlaceId && selectedPlaceId !== place.place_id) {
                    e.currentTarget.style.opacity = "0.8";
                  }
                }}
              >
                {/* Card content remains the same */}
                <div className="h-24 bg-gray-200 relative">
                  {place.photos && place.photos[0] ? (
                    <img
                      src={`https://maps.googleapis.com/maps/api/place/photo?maxwidth=400&photoreference=${place.photos[0].photo_reference}&key=${apiKey}`}
                      alt={place.name}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center bg-purple-100">
                      <span className="text-2xl">
                        {getCategoryIcon(activeTab)}
                      </span>
                    </div>
                  )}
                  {place.rating && (
                    <div className="absolute top-2 right-2 bg-white rounded-md px-1.5 py-0.5 flex items-center shadow-sm">
                      <span className="text-amber-500 mr-0.5">★</span>
                      <span className="text-xs font-medium">
                        {place.rating}
                      </span>
                    </div>
                  )}
                </div>
                <div className="p-3">
                  <h3 className="font-medium text-gray-800 text-sm mb-1 truncate">
                    {place.name}
                  </h3>
                  <div className="flex items-center space-x-2">
                    <div className="flex items-center bg-purple-100 px-2 py-0.5 rounded-sm">
                      <MapPin size={12} className="text-purple-700" />
                      <span className="text-xs text-purple-700 ml-1">
                        {place.distance} mi
                      </span>
                    </div>
                    {place.opening_hours && (
                      <span
                        className={`text-xs ${
                          place.opening_hours.open_now
                            ? "text-green-600"
                            : "text-red-600"
                        }`}
                      >
                        {place.opening_hours.open_now ? "Open" : "Closed"}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  };

  // Toggle view button component
  const ToggleViewButton = () => (
    <button
      className="flex items-center space-x-1 border border-gray-300 rounded-md px-3 py-1.5 bg-white"
      onClick={() => setViewMode(viewMode === "list" ? "map" : "list")}
    >
      <span className="text-sm">{viewMode === "list" ? "Map" : "List"}</span>
      {viewMode === "list" ? (
        <Map className="h-4 w-4" />
      ) : (
        <List className="h-4 w-4" />
      )}
    </button>
  );

  return (
    <MainLayout rs={false}>
      {loading && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-50 flex items-center justify-center z-50">
          <Loader2 className="animate-spin" width={64} height={64} />
        </div>
      )}

      <GoogleMapsScript apiKey={apiKey} />
      <ScrollbarStyles />

      <div className="flex justify-between items-center mb-4 pr-4 pl-4">
        <TabNavigation
          tabs={tabs}
          activeTab={activeTab}
          onTabChange={handleTabChange}
        />
        <div className="flex items-center space-x-1">
          <AddButton
            onClick={() => setShowAddBusiness(true)}
            className={
              useIsMobile()
                ? "fixed bottom-6 right-6 z-50 rounded-full w-14 h-14 flex items-center justify-center shadow-lg"
                : "flex items-center mx-auto"
            }
          >
            <Plus size={20} className={useIsMobile() ? "" : "mr-2"} />
            {!useIsMobile() && "Add New Business"}
          </AddButton>
          <ToggleViewButton />
        </div>
        {showAddBusiness && (
          <AddBusinessModal onClose={() => setShowAddBusiness(false)} />
        )}
      </div>

      <div className="pr-4 pl-4">
        {viewMode === "list" ? (
          <ContentView
            activeTab={activeTab}
            results={resultsWithDistanceAndImages}
            tryNewPlaces={tryNewPlaces}
            loading={loading}
            location={location}
          />
        ) : (
          <div className="relative h-[calc(100vh-120px)]">
            {error && <ErrorDisplay />}

            {/* Map Container */}
            <div ref={mapRef} className="h-full w-full" />

            {/* Loading Indicator */}
            {loading && (
              <div className="absolute inset-0 bg-white bg-opacity-70 flex items-center justify-center">
                <div className="flex flex-col items-center">
                  <div className="h-12 w-12 border-4 border-t-4 border-purple-500 rounded-full animate-spin" />
                  <p className="mt-4 text-lg">Loading places...</p>
                </div>
              </div>
            )}

            {/* Google Maps API Loading Indicator */}
            {!mapsLoaded && (
              <div className="absolute inset-0 bg-white bg-opacity-70 flex items-center justify-center">
                <div className="flex flex-col items-center">
                  <div className="h-12 w-12 border-4 border-t-4 border-blue-500 rounded-full animate-spin" />
                  <p className="mt-4 text-lg">Loading Google Maps...</p>
                </div>
              </div>
            )}

            {/* Distance Filter Section - Always visible when map is loaded */}
            {!loading && mapsLoaded && (
              <div
                className="absolute left-0 right-0 px-4 z-10"
                style={{
                  bottom: selectedPlaceId ? "220px" : "230px",
                  transition: "bottom 0.3s ease-in-out",
                }}
              >
                <div className="flex items-center space-x-2 overflow-x-auto hide-scrollbar">
                  {[1, 2, 5, 10, 15].map((mile) => (
                    <button
                      key={mile}
                      onClick={() => setCurrentMile(mile)}
                      className={`px-3 py-1.5 rounded-md flex-shrink-0 transition-all duration-200 ${
                        currentMile === mile
                          ? "bg-purple-700 text-white"
                          : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                      }`}
                    >
                      <span className="text-sm font-medium">{mile} mi</span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Horizontally Scrollable Cards - Only visible when there's data */}
            {!loading && resultsWithDistance.length > 0 && (
              <MapCardScroller
                places={resultsWithDistance}
                selectedPlaceId={selectedPlaceId}
                onCardClick={(place) => {
                  setSelectedPlace(place);

                  // Find and update the marker for this place
                  if (googleMapRef.current && markersRef.current) {
                    // Center map on the selected place with smooth animation
                    googleMapRef.current.panTo({
                      lat: place.geometry.location.lat,
                      lng: place.geometry.location.lng,
                    });

                    // Zoom in slightly for better visibility on mobile
                    if (isMobile && googleMapRef.current.getZoom() < 15) {
                      googleMapRef.current.setZoom(15);
                    }
                  }
                }}
              />
            )}

            {!loading && mapsLoaded && resultsWithDistance.length === 0 && (
              <div className="fixed inset-x-0 bottom-4 bg-white rounded-lg shadow-lg p-4 z-10 mx-2 sm:mx-auto sm:max-w-sm">
                <div className="text-center">
                  <p className="text-gray-600 text-sm">
                    No {activeTab.toLowerCase()} found within {currentMile} mile
                    {currentMile !== 1 ? "s" : ""} of your location.
                  </p>
                  <p className="text-gray-500 text-xs mt-1">
                    Try increasing the distance or selecting a different
                    category.
                  </p>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </MainLayout>
  );
};

export default DiscoverPage;
